/**
 * Utility function for uploading an individual file to S3 using presigned URLs.
 *
 * @param presignedUrl - The presigned URL from the backend.
 * @param file - The file to upload.
 * @param onProgress - Callback for upload progress updates (0-100).
 * @param abortSignal - Optional AbortSignal for cancellation support.
 * @returns Promise that resolves when upload is complete.
 */
export const uploadToS3 = (
  presignedUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  abortSignal?: AbortSignal,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    xhr.open('PUT', presignedUrl);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.statusText}`));
      }
    };

    xhr.onabort = () => {
      reject(new Error('ABORTED'));
    };

    xhr.onerror = () => {
      reject(new Error('Network error during upload'));
    };

    xhr.send(file);
  });
};
